import React, { useState, useEffect, useCallback } from 'react';
import { FileText } from 'lucide-react';
import { useAuth } from '../Auth/AuthContext';
import { PropertyReportModel, PropertyEntryModel } from '../../types/models';
import { FirebaseService } from '../../services/FirebaseService';
import './ReportViewer.css';

interface ReportViewerProps {
  reportId: string;
  onBack: () => void;
  onViewPDF?: (reportId: string) => void;
  isSharedReport?: boolean;
  onToggleSidebar?: () => void;
}

export const ReportViewer: React.FC<ReportViewerProps> = ({ reportId, onBack, onViewPDF, isSharedReport = false, onToggleSidebar }) => {
  const { user } = useAuth();
  const [report, setReport] = useState<PropertyReportModel | null>(null);
  const [entries, setEntries] = useState<PropertyEntryModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPDFViewer, setShowPDFViewer] = useState(false);
  


  const fetchReportData = useCallback(async () => {
    if (!user) return;

    setIsLoading(true);
    setError(null);
    try {
      if (isSharedReport) {
        // Fetch shared report data
        const sharedReports = await FirebaseService.getSharedReports();
        const foundReport = sharedReports.find(r => r.id === reportId);
        
        if (!foundReport) {
          setError('Shared report not found');
          return;
        }

        setReport(foundReport);

        // Fetch shared entries for this report
        const reportEntries = await FirebaseService.fetchSharedEntries(reportId);
        setEntries(reportEntries);
      } else {
        // Fetch personal report data (existing logic)
        const reports = await FirebaseService.fetchReports(user.uid);
        const foundReport = reports.find(r => r.id === reportId);
        
        if (!foundReport) {
          setError('Report not found');
          return;
        }

        setReport(foundReport);

        // Fetch entries for this report
        const reportEntries = await FirebaseService.fetchEntries(user.uid, reportId);
        setEntries(reportEntries);
      }

    } catch (error) {
      console.error('Error fetching report data:', error);
      setError(`Failed to load report: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  }, [user, reportId, isSharedReport]);

  useEffect(() => {
    if (user && reportId) {
      fetchReportData();
    }
  }, [user, reportId, fetchReportData]);



  if (isLoading) {
    return (
      <div className="report-viewer">
        <div className="loading-message">
          <h2>Loading Report...</h2>
          <p>Please wait while we fetch your report data.</p>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="report-viewer">
        <div className="error-message">
          <h2>Error Loading Report</h2>
          <p>{error || 'Report not found'}</p>
          <button onClick={onBack} className="btn btn-primary">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Show read-only PDF viewer when requested
  if (showPDFViewer) {
    // PDF Viewer component was removed - redirect to PDF editor instead
    if (onViewPDF) {
      onViewPDF(reportId);
      setShowPDFViewer(false);
    }
    return null;
  }

  return (
    <div className="report-viewer">
      <div className="report-header">
        <div className="header-actions">
          {onToggleSidebar && (
            <button className="mobile-menu-toggle" onClick={onToggleSidebar}>
              <span></span>
              <span></span>
              <span></span>
            </button>
          )}
          <button onClick={onBack} className="back-btn">
            ← Back to Reports
          </button>
          <div className="header-title">
            <h1>{report.title}</h1>
            <p className="report-meta">
              Created: {report.createdAt.toLocaleDateString()} | 
              Updated: {report.updatedAt.toLocaleDateString()} |
              Entries: {entries.length}
            </p>
          </div>
          <button 
            onClick={() => {
              if (onViewPDF) {
                onViewPDF(reportId);
              } else {
                setShowPDFViewer(true);
              }
            }}
            className="view-pdf-btn"
          >
            <FileText size={16} />
            View PDF
          </button>
        </div>
      </div>

      <div className="report-content">
        {entries.length === 0 ? (
          <div className="empty-state">
            <h3>No Entries Yet</h3>
            <p>This report doesn't have any inspection entries yet.</p>
            <button className="btn btn-primary">
              Add First Entry
            </button>
          </div>
        ) : (
          <div className="entries-grid">
            {entries.map((entry) => (
              <div key={entry.id} className="entry-card">
                <div className="entry-header">
                  <h3>{entry.title}</h3>
                  <span className="entry-date">
                    {entry.createdAt.toLocaleDateString()}
                  </span>
                </div>
                
                <div className="entry-content">
                  <p className="entry-note">{entry.note}</p>
                  
                  {entry.images && entry.images.length > 0 && (
                    <div className="entry-images">
                      <h4>Images ({entry.images.length})</h4>
                      <div className="images-grid">
                        {entry.images.map((image: any, index: number) => {
                          // Try different possible URL fields - note: Firebase stores as photo_url
                          const imageUrl = image.photo_url || image.photoURL || image.url || image.downloadURL || image.src;
                          
                          return (
                          <div key={image.id || index} className="image-item">
                            {imageUrl ? (
                              <>
                                <img 
                                  src={imageUrl} 
                                  alt={`Entry ${index + 1}`}
                                  className="entry-image"
                                  onClick={() => window.open(imageUrl, '_blank')}

                                  onError={(e) => {
                                    console.error('❌ Image failed to load:', imageUrl);
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = 'none';
                                  }}

                                />
                                {/* Simple metadata display below image */}
                                {(() => {
                                  const hasMetadata = image.metadata && (
                                    (image.metadata.latitude && image.metadata.longitude) ||
                                    (image.metadata.coordinates?.latitude && image.metadata.coordinates?.longitude) ||
                                    (image.metadata.lat && image.metadata.lng) ||
                                    (image.latitude && image.longitude) ||
                                    (image.lat && image.lng)
                                  );

                                  if (hasMetadata) {
                                    // Normalize metadata to consistent format
                                    const normalizedMetadata = {
                                      latitude: image.metadata?.latitude || 
                                               image.metadata?.coordinates?.latitude || 
                                               image.metadata?.lat || 
                                               image.latitude || 
                                               image.lat,
                                      longitude: image.metadata?.longitude || 
                                                image.metadata?.coordinates?.longitude || 
                                                image.metadata?.lng || 
                                                image.longitude || 
                                                image.lng,
                                      address: image.metadata?.address || 
                                              image.metadata?.location || 
                                              '',
                                      capturedAt: image.metadata?.capturedAt || new Date().toISOString()
                                    };

                                    return (
                                      <div className="simple-metadata-overlay" style={{
                                        background: 'rgba(0, 0, 0, 0.85)',
                                        color: 'white',
                                        padding: '8px 12px',
                                        borderRadius: '6px',
                                        marginTop: '8px',
                                        fontSize: '12px',
                                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
                                        display: 'flex',
                                        gap: '12px',
                                        alignItems: 'center'
                                      }}>
                                        {/* Actual map tile */}
                                        <div style={{
                                          width: '40px',
                                          height: '40px',
                                          borderRadius: '4px',
                                          border: '1px solid rgba(255, 255, 255, 0.3)',
                                          overflow: 'hidden',
                                          flexShrink: 0,
                                          backgroundColor: '#333',
                                          position: 'relative'
                                        }}>
                                          {(() => {
                                            // Simplified map tile approach
                                            const zoom = 17; // Higher zoom for more detail
                                            const lat = normalizedMetadata.latitude;
                                            const lon = normalizedMetadata.longitude;
                                            
                                            // Simple tile calculation
                                            const tileX = Math.floor((lon + 180.0) / 360.0 * Math.pow(2.0, zoom));
                                            const tileY = Math.floor((1.0 - Math.log(Math.tan(lat * Math.PI / 180.0) + 
                                              1.0 / Math.cos(lat * Math.PI / 180.0)) / Math.PI) / 2.0 * Math.pow(2.0, zoom));
                                            
                                            const tileUrl = `https://tile.openstreetmap.org/${zoom}/${tileX}/${tileY}.png`;
                                            
                                            return (
                                              <>
                                                <img
                                                  src={tileUrl}
                                                  alt="Map"
                                                  style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0
                                                  }}
                                                  onError={(e) => {
                                                    console.error('Map tile failed to load:', tileUrl);
                                                    const target = e.target as HTMLImageElement;
                                                    target.style.display = 'none';
                                                  }}
                                                />
                                                {/* Pin icon overlay */}
                                                <div style={{
                                                  position: 'absolute',
                                                  top: '50%',
                                                  left: '50%',
                                                  transform: 'translate(-50%, -50%)',
                                                  fontSize: '10px',
                                                  color: '#ff4444',
                                                  textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                                                  zIndex: 2,
                                                  pointerEvents: 'none'
                                                }}>
                                                  📍
                                                </div>
                                              </>
                                            );
                                          })()}
                                        </div>
                                        
                                        {/* Metadata text */}
                                        <div style={{ flex: 1 }}>
                                          {normalizedMetadata.address && (
                                            <div style={{ marginBottom: '4px', fontWeight: '500' }}>
                                              {normalizedMetadata.address}
                                            </div>
                                          )}
                                          <div style={{ fontSize: '11px', opacity: 0.8 }}>
                                            {new Date(normalizedMetadata.capturedAt).toLocaleDateString('en-US', {
                                              month: 'short',
                                              day: 'numeric',
                                              hour: 'numeric',
                                              minute: '2-digit',
                                              hour12: true
                                            })}
                                          </div>
                                          <div style={{ fontSize: '10px', opacity: 0.7, marginTop: '2px' }}>
                                            {normalizedMetadata.latitude?.toFixed(6)}, {normalizedMetadata.longitude?.toFixed(6)}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  }
                                  return null;
                                })()}
                              </>
                            ) : (
                              <div className="no-image">
                                <p>No valid image URL found</p>
                                <small>Available fields: {Object.keys(image).join(', ')}</small>
                              </div>
                            )}
                          </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Legacy single image support */}
                  {entry.photoURL && (!entry.images || entry.images.length === 0) && (
                    <div className="entry-images">
                      <h4>Image</h4>
                      <div className="image-item">
                        <img 
                          src={entry.photoURL} 
                          alt="Entry"
                          className="entry-image"
                          onClick={() => window.open(entry.photoURL!, '_blank')}

                          onError={(e) => {
                            console.error('❌ Legacy image failed to load:', entry.photoURL);
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                          // Add metadata attributes for legacy images
                          {...(() => {
                            const hasCoordinates = entry.latitude && entry.longitude;
                            if (hasCoordinates) {
                              const legacyMetadata = {
                                latitude: entry.latitude,
                                longitude: entry.longitude,
                                address: entry.address || '',
                                capturedAt: entry.createdAt?.toISOString() || new Date().toISOString()
                              };

                              return {
                                'data-has-metadata': 'true',
                                'data-metadata': JSON.stringify(legacyMetadata),
                                'data-image-id': `legacy-img-${entry.id || 'unknown'}`
                              };
                            }
                            return { 'data-has-metadata': 'false' };
                          })()}
                        />
                        {/* Legacy metadata now handled by overlays - no more text display */}
                      </div>
                    </div>
                  )}

                  {/* Entry-level metadata only if no images or for additional context */}
                  {(!entry.images || entry.images.length === 0) && !entry.photoURL && (
                    <div className="entry-metadata">
                      {entry.address && (
                        <div className="entry-location">
                          <strong>Location:</strong> {entry.address}
                        </div>
                      )}
                      
                      <div className="entry-coordinates">
                        <strong>Coordinates:</strong> {entry.latitude.toFixed(6)}, {entry.longitude.toFixed(6)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>


    </div>
  );
};
